import { MSUser } from "models/msUser";
import { useEffect, useRef, useState } from "react";
import graph from "services/graph";
import { MSAvatar } from "./MSAvatar";
import { Loader2, User2, XIcon } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

interface MSPickerProps {
    MicrosoftId?: string;
    onSelect?: (user:MSUser | null) => void;
    className?: string;
}
export function MSPicker({MicrosoftId, className, onSelect}: MSPickerProps) {
  const [loading, setLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<MSUser | null>(null);
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<MSUser[] | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation()

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  function handleSelect(user:MSUser | null) {
    if (onSelect) {
      onSelect(user);
      setSelectedPerson(user);
    }
  }

  useEffect(() => {
    setUsers(null);
    const delayDebounceFn = setTimeout(() => {
      if (query.length > 2) {
        setIsDropdownOpen(true);
        graph
          .get(
            `/users?$search="displayName:${query}"&$orderbydisplayName&$count=true`,
            {
              headers: {
                ConsistencyLevel: "eventual",
              },
            }
          )
          .then((res) => {
            if (res.data.value) {
                setUsers(res.data.value);
            }
          });
      } else {
        setIsDropdownOpen(false);
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  useEffect(() => {
    setSelectedPerson(null);
    const delayDebounceFn = setTimeout(() => {
      if (MicrosoftId) {
        if  (MicrosoftId.length > 1) {
          setLoading(true);
          graph.get(`/users/${MicrosoftId}`)
              .then(res => {
                setSelectedPerson(res.data)
                setLoading(false);
              })
              .catch(res => console.error(res.data))
        }
      }
    }, 100);
    return () => clearTimeout(delayDebounceFn);
  }, [MicrosoftId]);

  if (loading)
    return <div className="border text-zinc-600 border-zinc-400 gap-2 inline-flex w-full flex-1 items-center px-2 h-[35px] text-[15px] leading-none outline-none">
      <Loader2 className="animate-spin" />
      {t("microsoft.loading")}
    </div>

  return (
    <div className={clsx("relative", className)} ref={dropdownRef}>
      {selectedPerson ? (
        <div className="border border-zinc-400 gap-2 focus:border-black inline-flex w-full flex-1 items-center justify-center px-2 h-[35px] text-[15px] leading-none outline-none">
          <MSAvatar size={24} className="font-normal w-full" microsoftId={selectedPerson.id} showName />
          <button
            onClick={(_) => handleSelect(null)}
            className="p-1 text-zinc-500" 
          >
            <XIcon size={16} />
          </button>
        </div>
      ) : (
        <>
          <input
            className="border border-zinc-400 gap-2 focus:border-black inline-flex w-full flex-1 items-center justify-center h-[35px] px-2 py-1 text-[15px] leading-none outline-none"
            value={query}
            placeholder={t("microsoft.selectId")}
            onChange={(e) => setQuery(e.target.value)}
          />

          {isDropdownOpen && (
            <ul className="top-[35px] absolute z-10 w-full bg-white border border-zinc-400">
              { users ? 
              users.map((user) => (
                <li
                  onClick={(_) => handleSelect(user)}
                  className="flex text-sm gap-2 p-2 items-center cursor-default hover:bg-primary/10 w-full"
                >
                  <MSAvatar microsoftId={user.id} msUser={user} subtitle={user.mail} showName />
                </li>
              ))
            : <li className="p-4">
                <Loader2 className="animate-spin text-zinc-500"/>
            </li>}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
